/**
 * De QUÉ es un recurso a DÓNDE se abre (§9).
 *
 * El backend nunca devuelve rutas. Devuelve la identidad de lo que produjo
 * --`{type: "participant", id}`-- y esta tabla es el único sitio que
 * sabe traducirlo. Mover una pantalla es cambiar una línea aquí, sin tocar el
 * backend ni las conversaciones ya guardadas, donde el enlace vive como tipo e
 * id y se resuelve al pintarlo, no al escribirlo.
 *
 * Es una función pura y vive fuera de los componentes a propósito: es la
 * regla, no el estado, y así se prueba sin montar la aplicación. Si algún día
 * un enlace tiene que salir de la app --un correo, una notificación-- se le
 * pone delante una ruta genérica que llame a esto; no hay que rehacer nada.
 */
import type { AgentPart } from "../chat/agent-event.ts";

export type Resource = Extract<
  AgentPart,
  { type: "resource_link" }
>["resource"];

/** Adónde navegar, en el vocabulario de TanStack Router. */
export interface ResourceTarget {
  readonly to: string;
  readonly params?: Record<string, string>;
  readonly search?: Record<string, string>;
}

/**
 * Cada tipo, su pantalla. Devolver null es una respuesta válida: significa
 * "conozco este tipo pero no tiene pantalla propia".
 */
const ROUTES: Record<
  string,
  (id: string | undefined) => ResourceTarget | null
> = {
  /**
   * Todavía no hay pantalla de participantes: se conoce el tipo, pero no se
   * abre. Cuando exista, esta línea dice a dónde lleva.
   */
  participant: () => null,

  /** Un periodo no es una pantalla: es el mes que mira todo el panel. */
  period: (id) =>
    id === undefined ? { to: "/work" } : { to: "/work", search: { p: id } },
};

export const resourceTarget = (resource: Resource): ResourceTarget | null =>
  ROUTES[resource.type]?.(resource.id) ?? null;

/**
 * Si este frontend sabe abrir ese tipo.
 *
 * Es lo que permite que el backend empiece a mandar tipos nuevos antes de que
 * aquí existan: en vez de reventar o de no dibujar nada --que se lee igual que
 * un fallo-- se enseña el enlace apagado diciendo que esta versión todavía no
 * sabe abrirlo.
 */
export const isKnownResource = (resource: Resource): boolean =>
  resource.type in ROUTES;
